// Cross-subdomain consent state, shared with the Finsweet Consent Pro `fs-consent`
// cookie on `.soaak.com`. A choice made on soaak.com is honored here (inbound), and a
// choice made in the app is written back to `.soaak.com` (outbound).
//
// NOTE: the exact `fs-consent` schema written by Consent Pro must be confirmed with the
// Aqui/Consent Pro side (or observed on a live soaak.com session). Reading is tolerant;
// the write shape below mirrors the `choices.{analytics,marketing}` keys already used by
// the GTM "Finsweet - Analytics/Marketing Consent" variables.

export type ConsentChoices = {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  personalization: boolean;
};

const COOKIE_NAME = 'fs-consent';
const MAX_AGE_SECONDS = 180 * 24 * 60 * 60;

const DENIED: ConsentChoices = {
  essential: true,
  analytics: false,
  marketing: false,
  personalization: false,
};

type Listener = (consent: ConsentChoices) => void;
const listeners = new Set<Listener>();

const rootDomain = (): string => {
  const parts = window.location.hostname.split('.');
  return parts.length > 1 ? `.${parts.slice(-2).join('.')}` : window.location.hostname;
};

const readCookie = (name: string): string => {
  const row = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));
  return row ? decodeURIComponent(row.slice(name.length + 1)) : '';
};

export const getConsent = (): ConsentChoices | null => {
  try {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const choices = parsed && parsed.choices ? parsed.choices : parsed;
    return {
      essential: true,
      analytics: choices.analytics === true,
      marketing: choices.marketing === true,
      personalization: choices.personalization === true,
    };
  } catch {
    return null;
  }
};

export const hasChosen = (): boolean => getConsent() !== null;
export const marketingGranted = (): boolean => getConsent()?.marketing === true;
export const analyticsGranted = (): boolean => getConsent()?.analytics === true;

// Google Consent Mode v2 — reflect the choice so GTM's Google tags react to it.
const pushConsentMode = (consent: ConsentChoices): void => {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer.push(arguments);
  }
  (gtag as any)('consent', 'update', {
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    personalization_storage: consent.personalization ? 'granted' : 'denied',
  });
};

export const setConsent = (choices: Partial<ConsentChoices>): ConsentChoices => {
  const next: ConsentChoices = {
    ...DENIED,
    ...(getConsent() || {}),
    ...choices,
    essential: true,
  };
  const payload = JSON.stringify({ choices: next, timestamp: Date.now() });
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    `${COOKIE_NAME}=${encodeURIComponent(payload)}; Max-Age=${MAX_AGE_SECONDS}` +
    `; Path=/; Domain=${rootDomain()}; SameSite=Lax${secure}`;
  pushConsentMode(next);
  listeners.forEach((listener) => {
    try {
      listener(next);
    } catch {
      /* a listener must not break consent propagation */
    }
  });
  return next;
};

export const acceptAll = (): ConsentChoices =>
  setConsent({ analytics: true, marketing: true, personalization: true });

export const rejectAll = (): ConsentChoices =>
  setConsent({ analytics: false, marketing: false, personalization: false });

export const onConsentChange = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

// Run `fn` once marketing consent is (or later becomes) granted.
export const whenMarketing = (fn: () => void): void => {
  if (marketingGranted()) {
    fn();
    return;
  }
  const off = onConsentChange((consent) => {
    if (consent.marketing) {
      off();
      fn();
    }
  });
};
